/**
 * Filter builder — creates FilterDef objects with type-appropriate defaults.
 *
 * Each builder sets the `defaultOperator` and `toParam` function so that
 * the filter knows how to serialize itself to API query params without
 * any external mapping tables.
 *
 * Usage:
 *   f.text('number', { label: 'Order Number', group: 'order' })
 *   f.select('status.code', { label: 'Status', multi: true, options: [...] })
 *   f.date('created_at', { label: 'Date Created', group: 'order' })
 */

import type { FilterDef, FilterOption, FilterType } from "./types";
import type { TranslateResult } from "vue-i18n";
import { defaultToParam } from "./helpers";

/** Wrap static options in a promise-returning function */
function wrapOptions(
  opts?: FilterOption[] | (() => Promise<FilterOption[]>)
): (() => Promise<FilterOption[]>) | undefined {
  if (!opts) return undefined;
  if (typeof opts === "function") return opts;
  return () => Promise.resolve(opts);
}

/** Generate a stable def ID from field + type */
function defId(field: string, type: FilterType): string {
  return `${type}:${field}`;
}

// ─── Common options type ────────────────────────────────────────────────────

interface BaseOpts {
  label: string | TranslateResult;
  group?: string;
  icon?: string;
  allowMultiple?: boolean;
  requires?: Record<string, string>;
  if?: () => boolean;
}

// ─── Builders ───────────────────────────────────────────────────────────────

/**
 * Text search filter — uses `like` operator.
 * Wraps value in `%…%` for contains matching.
 */
function text(
  field: string,
  opts: BaseOpts & {
    operators?: string[];
  }
): FilterDef {
  return {
    id: defId(field, "text"),
    field,
    type: "text",
    label: opts.label,
    group: opts.group,
    icon: opts.icon,
    allowMultiple: opts.allowMultiple,
    requires: opts.requires,
    if: opts.if,
    defaultOperator: "like",
    operators: opts.operators || ["like", "eq", "neq"],
    toParam: (value: any, operator?: string) => {
      const op = operator || "like";
      const suffix = op && op !== "eq" ? `|${op}` : "";
      let formatted = String(value);
      if (op === "like") formatted = `%${formatted}%`;
      return { [`filter[${field}${suffix}]`]: formatted };
    }
  };
}

/**
 * Exact match text filter — uses default (equality) operator.
 */
function exact(field: string, opts: BaseOpts): FilterDef {
  return {
    id: defId(field, "exact"),
    field,
    type: "exact",
    label: opts.label,
    group: opts.group,
    icon: opts.icon,
    allowMultiple: opts.allowMultiple,
    requires: opts.requires,
    if: opts.if,
    defaultOperator: "eq",
    toParam: defaultToParam(field, "eq")
  };
}

/**
 * Numeric filter — supports eq, gt, gte, lt, lte operators.
 */
function number(
  field: string,
  opts: BaseOpts & {
    operators?: string[];
  }
): FilterDef {
  return {
    id: defId(field, "number"),
    field,
    type: "number",
    label: opts.label,
    group: opts.group,
    icon: opts.icon,
    allowMultiple: opts.allowMultiple ?? true,
    requires: opts.requires,
    if: opts.if,
    defaultOperator: "eq",
    operators: opts.operators || ["eq", "neq", "gt", "gte", "lt", "lte"],
    toParam: defaultToParam(field, "eq")
  };
}

/**
 * Select filter — single or multi value.
 * Options are always resolved via promise.
 */
function select(
  field: string,
  opts: BaseOpts & {
    multi?: boolean;
    options?: FilterOption[] | (() => Promise<FilterOption[]>);
    operators?: string[];
  }
): FilterDef {
  // If options are a static array, cache them immediately for pill labels
  const staticOptions = Array.isArray(opts.options) ? opts.options : undefined;
  return {
    id: defId(field, "select"),
    field,
    type: "select",
    label: opts.label,
    group: opts.group,
    icon: opts.icon,
    multi: opts.multi,
    allowMultiple: opts.allowMultiple,
    requires: opts.requires,
    if: opts.if,
    defaultOperator: "eq",
    operators: opts.operators || ["eq", "neq"],
    options: wrapOptions(opts.options),
    staticOptions,
    toParam: defaultToParam(field, "eq")
  };
}

/**
 * Boolean filter — renders as a toggle/radio.
 * Value is `"1"` or `"0"`.
 */
function boolean(field: string, opts: BaseOpts): FilterDef {
  return {
    id: defId(field, "boolean"),
    field,
    type: "boolean",
    label: opts.label,
    group: opts.group,
    icon: opts.icon,
    requires: opts.requires,
    if: opts.if,
    defaultOperator: "eq",
    options: wrapOptions([
      { value: "1", label: "Yes" },
      { value: "0", label: "No" }
    ]),
    toParam: defaultToParam(field, "eq")
  };
}

/**
 * Date filter — supports gt, gte, lt, lte, after, before operators.
 * `allowMultiple` defaults to true (e.g. date > X AND date < Y).
 */
function date(
  field: string,
  opts: BaseOpts & {
    operators?: string[];
  }
): FilterDef {
  return {
    id: defId(field, "date"),
    field,
    type: "date",
    label: opts.label,
    group: opts.group,
    icon: opts.icon,
    allowMultiple: opts.allowMultiple ?? true,
    requires: opts.requires,
    if: opts.if,
    defaultOperator: "gt",
    operators: opts.operators || ["gt", "gte", "lt", "lte", "after", "before"],
    toParam: defaultToParam(field, "gt")
  };
}

/**
 * Tag filter — renders a tag cloud with any/all/none operators.
 * Values are always arrays of tag IDs (comma-separated in API).
 */
function tag(
  field: string,
  opts: BaseOpts & {
    options?: FilterOption[] | (() => Promise<FilterOption[]>);
    operators?: string[];
  }
): FilterDef {
  const staticOptions = Array.isArray(opts.options) ? opts.options : undefined;
  return {
    id: defId(field, "tag"),
    field,
    type: "tag",
    label: opts.label,
    group: opts.group,
    icon: opts.icon,
    multi: true,
    requires: opts.requires,
    if: opts.if,
    defaultOperator: "any",
    operators: opts.operators || ["any", "all", "none"],
    options: wrapOptions(opts.options),
    staticOptions,
    toParam: defaultToParam(field, "any")
  };
}

/**
 * Async search (typeahead) filter.
 * Options are loaded via `searchFn` on each keystroke.
 * Values restored from URL are resolved via `resolveFn`.
 */
function async(
  field: string,
  opts: BaseOpts & {
    searchFn: (query: string, offset?: number) => Promise<FilterOption[]>;
    resolveFn?: (ids: string[]) => Promise<FilterOption[]>;
    multi?: boolean;
  }
): FilterDef {
  return {
    id: defId(field, "async"),
    field,
    type: "async",
    label: opts.label,
    group: opts.group,
    icon: opts.icon,
    multi: opts.multi,
    allowMultiple: opts.allowMultiple,
    requires: opts.requires,
    if: opts.if,
    defaultOperator: "eq",
    searchFn: opts.searchFn,
    resolveFn: opts.resolveFn,
    toParam: defaultToParam(field, "eq")
  };
}

/**
 * Category tree selector filter — renders the vue-treeselect input.
 * Multi-select; semantics match the legacy `ProductCategoryFilter`
 * (default operator `any`).
 */
function category(
  field: string,
  opts: BaseOpts & {
    options?: FilterOption[] | (() => Promise<FilterOption[]>);
    operators?: string[];
  }
): FilterDef {
  const staticOptions = Array.isArray(opts.options) ? opts.options : undefined;
  return {
    id: defId(field, "category"),
    field,
    type: "category",
    label: opts.label,
    group: opts.group,
    icon: opts.icon,
    multi: true,
    requires: opts.requires,
    if: opts.if,
    defaultOperator: "any",
    operators: opts.operators || ["any", "all", "none"],
    options: wrapOptions(opts.options),
    staticOptions,
    toParam: defaultToParam(field, "any")
  };
}

/**
 * Raw/custom filter — you control field, operator, and serialisation.
 */
function raw(
  field: string,
  opts: BaseOpts & {
    operator?: string;
    operators?: string[];
    options?: FilterOption[] | (() => Promise<FilterOption[]>);
    toParam?: FilterDef["toParam"];
  }
): FilterDef {
  const op = opts.operator || "eq";
  return {
    id: defId(field, "raw"),
    field,
    type: "raw",
    label: opts.label,
    group: opts.group,
    icon: opts.icon,
    allowMultiple: opts.allowMultiple,
    requires: opts.requires,
    if: opts.if,
    defaultOperator: op,
    operators: opts.operators,
    options: wrapOptions(opts.options),
    toParam: opts.toParam || defaultToParam(field, op)
  };
}

// ─── Export ─────────────────────────────────────────────────────────────────

export const f = {
  text,
  exact,
  number,
  select,
  boolean,
  date,
  tag,
  async,
  category,
  raw
} as const;
